/**
 * UpdateAvailableToast - gentle "a new version is available" prompt.
 *
 * Phase: SPA cache-busting (2026-05-28).
 *
 * Renders a non-blocking snackbar at the bottom of the viewport when
 * useVersionUpdate detects that a newer frontend has been deployed since this
 * tab booted. The user chooses when to reload - we never auto-reload, because a
 * veteran could be mid-task (e.g. drafting an appeal letter) and a forced
 * reload would discard their work.
 *
 * Behaviour:
 *   - Appears once an update is detected; persists (no auto-hide) so it isn't
 *     missed, but is dismissible via the close (x) action.
 *   - "Reload" performs a full window reload, which re-fetches index.html and
 *     picks up the new bundle.
 *   - Once dismissed, it stays dismissed for the life of the tab (the user has
 *     acknowledged; nagging is worse than waiting for their next natural reload).
 *
 * @component
 */
import React, { useState } from 'react';
import { Snackbar, Alert, Button, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import useVersionUpdate from '../hooks/useVersionUpdate';

export default function UpdateAvailableToast() {
  const { updateAvailable } = useVersionUpdate();
  const [dismissed, setDismissed] = useState(false);

  const open = updateAvailable && !dismissed;

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      data-testid="update-available-toast"
    >
      <Alert
        severity="info"
        variant="filled"
        role="status"
        sx={{ alignItems: 'center' }}
        action={
          <>
            <Button
              color="inherit"
              size="small"
              onClick={handleReload}
              data-testid="update-reload-button"
              sx={{ fontWeight: 700, textTransform: 'none' }}
            >
              Reload
            </Button>
            <IconButton
              color="inherit"
              size="small"
              aria-label="Close"
              onClick={() => setDismissed(true)}
              data-testid="update-dismiss-button"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        A new version of AfterService is available.
      </Alert>
    </Snackbar>
  );
}
